// Meta (Facebook) Pixel integration.
// Kept in one place so the pixel is initialised exactly once and every event is
// fired through a single, well-behaved code path.
export const FB_PIXEL_ID = '2254304005331148'
const FBEVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js'
type PixelWindow = Window & {
  fbq?: any
  _fbq?: any
  __rugbyPixelInitialised?: boolean
}
function getWindow(): PixelWindow | null {
  return typeof window === 'undefined' ? null : (window as PixelWindow)
}
/**
 * Creates the fbq stub + queue and injects fbevents.js. Safe to call more than
 * once: the stub is only ever created if it does not already exist.
 */
function ensureFbqStub(w: PixelWindow): void {
  if (w.fbq) return
  const n: any = (w.fbq = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args)
  })
  if (!w._fbq) w._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []
  const script = document.createElement('script')
  script.async = true
  script.src = FBEVENTS_SRC
  script.onerror = () => {
    /* blocked by an ad blocker or offline — never break the page */
  }
  ;(document.head || document.documentElement).appendChild(script)
}
/**
 * Initialises the pixel and sends the single PageView for this page load.
 * Guarded by a window flag so React strict-mode double effects, hot reloads or
 * a second mount can never double-count a PageView.
 */
export function initMetaPixel(): void {
  const w = getWindow()
  if (!w || w.__rugbyPixelInitialised) return
  try {
    ensureFbqStub(w)
    w.__rugbyPixelInitialised = true
    w.fbq('init', FB_PIXEL_ID)
    w.fbq('track', 'PageView')
  } catch {
    /* never let analytics break the page */
  }
}
/** Fires a standard pixel event. Returns false when the pixel is unavailable. */
export function trackPixelEvent(
  event: string,
  params: Record<string, unknown> = {},
): boolean {
  const w = getWindow()
  if (!w?.fbq) return false
  try {
    // A stable event id lets Meta de-duplicate this browser event against the
    // same order arriving from the checkout / server side.
    const eventID = `${event.toLowerCase()}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`
    w.fbq('track', event, params, {
      eventID,
    })
    return true
  } catch {
    return false
  }
}
/**
 * Fires an event and THEN navigates. Redirecting in the same tick as the event
 * cancels the in-flight request, which silently loses conversions — so we give
 * the beacon a short window to leave the browser before unloading the page.
 */
export function trackEventThenNavigate(
  event: string,
  url: string,
  params: Record<string, unknown> = {},
): void {
  const w = getWindow()
  if (!w) return
  const go = () => {
    w.location.href = url
  }
  const sent = trackPixelEvent(event, params)
  if (!sent) {
    go()
    return
  }
  // 300ms is imperceptible to the user but enough for the request to be sent.
  w.setTimeout(go, 300)
}
